import React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from '../lib/toast';

// Grantor-side De-Scope Review.
//
// A subgrantee asks to be released from N awarded passings as "too far / too expensive".
// This screen answers the two questions a state broadband office actually has to settle:
// what would it really cost to reach them, and what is the exposure if we let them go.
//
// The cost comes from the same design engine the subgrantee runs, which is the whole
// point: the dispute collapses to one shared number instead of duelling spreadsheets.
//
// What this screen is NOT is a calculator. The number goes into an administrative record
// and gets defended years later, so every caveat the engine produces - whose rate card
// priced it, how many of the requested passings the design actually reached, what the
// method excludes, what the router warned about - is rendered here, at the same weight
// as the dollar figure. A caveat that lives only in the HTTP response body is a caveat
// the reviewer does not have when counsel calls.

const API_BASE = (import.meta as any).env?.VITE_API_BASE ?? '/api';

type Row = {
  award_id: string; state: string; subgrantee: string;
  project_id: string; project_code: string; project_name: string;
  awarded_bsl_count: number | null; grant_amount_usd: string | null;
  awarded_locations: string;
};
type Loc = {
  fcc_location_id: string; address: string | null; city: string | null;
  lng: number; lat: number;
};
type Assumptions = {
  source?: string | null;
  rate_card_name?: string | null;
  rate_card_id?: string | null;
  region_mult?: number | null;
  plant_settings_from?: string | null;
};
type PassingsBasis = {
  requested?: number | null;
  counted?: number | null;
  reached?: number | null;
  unreached?: string[] | null;
  // The exact set the review priced, frozen into the record. A release is drawn from
  // this list, never re-derived from the award (which may have moved since).
  resolved_ids?: string[] | null;
  basis?: string | null;
};
// Item 4: the backend now declares BOTH denominators explicitly instead of shipping
// one in the headline and the other buried in JSON. Older reviews (already on this box)
// have only the flat fields, so every read here falls back.
type CostBasis = {
  cost_per_passing_usd?: number | null;
  federal_at_risk_usd?: number | null;
  build_vs_award_ratio?: number | null;
  n?: number | null;
};
type Review = {
  review_id?: string | null;
  disclaimer: string;
  excluded_requested: number; excluded_resolved: number; seeded_from: string;
  method?: string | null;
  method_note?: string | null;
  assumptions?: Assumptions | null;
  passings_basis?: PassingsBasis | null;
  structures_passed_by_designed_plant?: number | null;
  shared_with_retained?: number | null;
  inputs_fingerprint?: any;
  design: {
    nodes?: number; segments?: number; length_km?: number | null;
    warnings?: string[] | null;
  };
  cost: {
    total_cost_usd: number;
    cost_per_passing_usd: number | null;
    cost_per_passing_denominator?: string | null;
    cost_per_counted_passing_usd?: number | null;
    // Item 16: the allocation bracket. Standalone charges 100% of shared plant to the
    // excluded set; allocated spreads it over everything the plant passes. The true
    // marginal cost is between them.
    cost_per_passing_standalone_usd?: number | null;
    cost_per_passing_allocated_usd?: number | null;
  };
  exposure: {
    federal_at_risk_usd: number; award_rate_per_passing_usd: number | null;
    award_rate_basis: string; build_vs_award_ratio: number | null;
  };
  bases?: { reached?: CostBasis | null; counted?: CostBasis | null } | null;
};
type DecisionRow = {
  id?: string;
  review_id?: string;
  decision: string;
  rationale: string;
  decided_by?: string | null;
  decided_by_email?: string | null;
  decided_by_name?: string | null;
  decided_at?: string | null;
  released_ids?: string[] | null;
  unreached_rationale?: string | null;
  reversed_at?: string | null;
  reversed_by_name?: string | null;
  reversal_rationale?: string | null;
};
type CurvePoint = {
  n: number; cost_total: number; cost_per_passing: number; ratio?: number | null;
};
type Curve = {
  points: CurvePoint[];
  breakeven_n?: number | null;
  breakeven_cost_per_passing?: number | null;
  // Item 16: a bare null breakeven reads as "there is no crossing". Sometimes it means
  // "this method cannot tell you", which is a different sentence in a federal record.
  breakeven_unavailable_reason?: string | null;
  ordering?: string | null;
};

const usd = (v: number | null | undefined, dp = 0) =>
  v == null || !Number.isFinite(Number(v))
    ? '—'
    : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: dp });

const DECISIONS: Array<[string, string]> = [
  ['grant_release', 'Grant release'],
  ['deny_hold_to_contract', 'Deny — hold to contract'],
  ['partial_release_outliers_only', 'Partial — outliers only'],
  ['rescope_alternative_technology', 'Re-scope to alt technology'],
];
const decisionLabel = (k: string) =>
  DECISIONS.find(([x]) => x === k)?.[1] ?? k.replace(/_/g, ' ');

const RATIONALE_MIN = 20;

// The engine names its seed strategy in snake_case; a determination reads better in
// English, and this text is quoted in the PDF and the ledger.
function seededFromProse(s: string | null | undefined): string {
  if (!s) return 'Seed point for the design was not reported.';
  const pretty = String(s).replace(/_/g, ' ');
  return `The design was seeded from ${pretty}.`;
}



// What a reviewer is looking at when they open the register.
//
// "Open matters" is the default because it is the only one that is a to-do list: a
// subgrantee has asked for something and this office has not answered. Analysis runs
// are the desk's own working paper - real evidence, but nobody is waiting on them - so
// they are one click away rather than mixed into the queue.
const SCOPES: Array<{ key: string; label: string; test: (r: any) => boolean }> = [
  { key: 'open',     label: 'Open matters',  test: (r) => r.status === 'open' && r.received_via !== 'analysis' },
  { key: 'decided',  label: 'Decided',       test: (r) => !!r.decision },
  { key: 'analysis', label: 'Analysis runs', test: (r) => r.received_via === 'analysis' },
  { key: 'all',      label: 'Everything',    test: () => true },
];


// The case number for a review we may only know by id - the register already has it,
// so a freshly-run review shows its number without a second fetch.
function reviewNoOf(runs: any[], id: string | null): string | null {
  if (!id) return null;
  return runs.find((r: any) => r.id === id)?.review_no ?? null;
}

const RUN_TS = (v: any) => {
  if (!v) return '—';
  const d = new Date(v);
  // UTC on purpose. A determination is an administrative record and the reviewer, the
  // subgrantee and the auditor are rarely in the same timezone.
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)}Z`;
};

function runStatus(r: any): { text: string; cls: string } {
  if (r.reversed) return { text: 'Reversed', cls: 'pill pill--danger' };
  if (r.decision === 'grant_release') return { text: 'Released', cls: 'pill pill--success' };
  if (r.decision === 'partial_release_outliers_only') return { text: 'Partial release', cls: 'pill pill--warning' };
  if (r.decision === 'deny') return { text: 'Denied', cls: 'pill pill--danger' };
  if (r.decision) return { text: String(r.decision).replace(/_/g, ' '), cls: 'pill pill--info' };
  if (r.status === 'withdrawn') return { text: 'Withdrawn', cls: 'pill' };
  // Short. A two-word status that wraps onto two lines in its own pill reads as
  // a layout bug, not as a status.
  return { text: 'Undecided', cls: 'pill pill--info' };
}

// The record of what this office has run and signed. Every row is a persisted review:
// nothing here is recomputed on demand, and the document prints the review as it was
// run, fingerprint and all.
function RunRecord({ runs, loading, projectId, activeId, onOpen, onDelete, busy, primary }: any) {
  // Open by default when it IS the page; collapsed when it is a footer under a run.
  const [open, setOpen] = useState(!!primary);
  const [scope, setScope] = useState('open');
  const [who, setWho] = useState('');

  const scoped = useMemo(() => {
    const t = (SCOPES.find((x) => x.key === scope) ?? SCOPES[3]).test;
    return runs.filter((r: any) => t(r) && (!who || r.subgrantee_label === who));
  }, [runs, scope, who]);

  // Counts come off the unfiltered list so a tab can say "0" instead of vanishing -
  // an empty queue is information, and a tab that disappears when it empties hides it.
  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const sc of SCOPES) m[sc.key] = runs.filter((r: any) => sc.test(r)).length;
    return m;
  }, [runs]);

  const subgrantees = useMemo(
    () => [...new Set(runs.map((r: any) => r.subgrantee_label).filter(Boolean))].sort(),
    [runs],
  );
  const [all, setAll] = useState(false);
  // Ten is what a reviewer scans; the rest is on demand. An uncapped table here is
  // what pushed the actual review four screens down the page.
  const shown = all || primary ? scoped : scoped.slice(0, 10);
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card__header">
        <span>
          Record of runs{projectId ? '' : ' — all projects'}
          <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · {scoped.length}</span>
        </span>
        <button className="btn" onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && primary && (
        <div className="card__body" style={{
          display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
          borderBottom: '1px solid var(--border)',
        }}>
          {SCOPES.map((sc) => (
            <button key={sc.key}
                    className={`btn${scope === sc.key ? ' btn--primary' : ''}`}
                    onClick={() => setScope(sc.key)}>
              {sc.label} · {counts[sc.key] ?? 0}
            </button>
          ))}
          <select value={who} onChange={(e) => setWho(e.target.value)} style={{ marginLeft: 'auto' }}>
            <option value="">All subgrantees</option>
            {subgrantees.map((x: any) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
      )}
      {open && (
        <div style={{ overflow: 'auto', maxHeight: primary ? 'calc(100vh - 380px)' : 360 }}>
          {loading ? (
            <div className="card__body" style={{ color: 'var(--text-muted)' }}>Loading…</div>
          ) : scoped.length === 0 ? (
            <div className="card__body" style={{ color: 'var(--text-muted)' }}>
              {runs.length === 0
                ? `No reviews have been run${projectId ? ' for this award' : ''} yet.`
                : scope === 'open'
                  ? 'Nothing is open on this desk. Every matter has been answered.'
                  : 'Nothing matches this filter.'}
            </div>
          ) : (
            <table className="table" style={{ fontVariantNumeric: 'tabular-nums' }}>
              <thead>
                <tr>
                  <th style={{ whiteSpace: 'nowrap' }}>Review</th>
                  <th>Run (UTC)</th>
                  <th>Subgrantee</th>
                  <th style={{ textAlign: 'right' }}>Requested</th>
                  <th style={{ textAlign: 'right' }}>Reached</th>
                  <th style={{ textAlign: 'right' }}>Cost</th>
                  <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>vs award</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Status</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Determination</th>
                  <th>Record</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r: any) => {
                  const st = runStatus(r);
                  const ratio = r.ratio != null ? Number(r.ratio) : null;
                  return (
                    <tr key={r.id}
                        onClick={() => onOpen(r)}
                        title="Open this run"
                        style={{
                          cursor: 'pointer',
                          background: r.id === activeId ? 'var(--surface-alt)' : undefined,
                        }}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, whiteSpace: 'nowrap', fontWeight: 700 }}>
                        {r.review_no ?? String(r.id).slice(0, 8)}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, whiteSpace: 'nowrap' }}>{RUN_TS(r.run_at)}</td>
                      <td>{r.subgrantee_label ?? r.project_code ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>{r.requested_n ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>{r.reached_n ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        {r.cost_total != null
                          ? Number(r.cost_total).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
                          : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>{ratio != null ? `${ratio.toFixed(2)}\u00d7` : '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}><span className={st.cls}>{st.text}</span></td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {r.decided_at ? (
                          <>
                            <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)', whiteSpace: 'nowrap' }}>
                              {r.reversal_no ?? r.determination_no ?? '—'}
                            </div>
                            <div>{RUN_TS(r.decided_at)}{r.decided_by_name ? ` · ${r.decided_by_name}` : ''}</div>
                          </>
                        ) : '—'}
                      </td>
                      {/* The row itself opens the run, so these must not also trigger it. */}
                      <td style={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        <a className="btn" href={`${API_BASE}/descope/reviews/${r.id}/determination.pdf`}
                           target="_blank" rel="noreferrer">PDF</a>{' '}
                        <a className="btn" href={`${API_BASE}/descope/reviews/${r.id}/determination.csv`}
                           target="_blank" rel="noreferrer">CSV</a>{' '}
                        {r.decision ? (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }} title="A determination rests on this run">
                            on the record
                          </span>
                        ) : (
                          <button className="btn" disabled={busy}
                                  onClick={() => {
                                    if (confirm(`Discard ${r.review_no ?? 'this run'}? `
                                              + 'It has no determination on it, so nothing on the record points at it.')) {
                                      onDelete(r.id);
                                    }
                                  }}>
                            Discard
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div className="card__body" style={{ fontSize: 12, color: 'var(--text-muted)', paddingTop: 8,
                                              display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ flex: 1 }}>
              Click a run to open it. Every run is kept until you discard it; one a
              determination rests on cannot be discarded, only reversed.
            </span>
            {runs.length > 10 && !primary && (
              <button className="btn" onClick={() => setAll((v) => !v)}>
                {all ? 'Show 10 most recent' : `Show all ${runs.length}`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// What a reviewer can do with a matter SHORT of deciding it. A determination is the end
// of the road; most of a request's life is before that, and an oversight desk that can
// only sign or do nothing is not an oversight desk.
function MatterActions({ matter, assignees, onPatch, busy }: any) {
  const [who, setWho] = useState<string>('');
  const [closing, setClosing] = useState(false);
  const [why, setWhy] = useState('');
  useEffect(() => { setWho(matter?.assigned_to ?? ''); }, [matter?.assigned_to]);
  if (!matter) return null;

  const overdue = matter.due_by && new Date(matter.due_by) < new Date();
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card__header">
        <span>The matter</span>
        <span className={`pill ${overdue ? 'pill--danger' : 'pill--info'}`}>
          {matter.status === 'open' ? (overdue ? 'Open · overdue' : 'Open') : matter.status}
        </span>
      </div>
      <div className="card__body" style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>
            Response due
          </div>
          <b style={{ color: overdue ? 'var(--danger)' : undefined }}>
            {matter.due_by ? String(matter.due_by).slice(0, 10) : 'not set'}
          </b>
        </div>
        <div style={{ minWidth: 240 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>
            Assigned to — escalate by handing it on
          </div>
          <select value={who} disabled={busy}
                  onChange={(e) => { setWho(e.target.value); onPatch({ assigned_to: e.target.value || null }); }}
                  style={{ width: '100%' }}>
            <option value="">Unassigned</option>
            {assignees.map((a: any) => (
              <option key={a.id} value={a.id}>{a.name || a.email} — {a.role}</option>
            ))}
          </select>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          {!closing ? (
            <button className="btn" disabled={busy} onClick={() => setClosing(true)}>
              Close without a determination
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input value={why} onChange={(e) => setWhy(e.target.value)} disabled={busy}
                     placeholder="Why — this goes on the record" style={{ width: 320 }} />
              <button className="btn" disabled={busy} onClick={() => { setClosing(false); setWhy(''); }}>
                Cancel
              </button>
              <button className="btn btn--primary" disabled={busy || why.trim().length < 5}
                      onClick={() => onPatch({ status: 'withdrawn', withdrawn_reason: why.trim() })}>
                Close it
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="card__body" style={{ paddingTop: 0, fontSize: 12, color: 'var(--text-muted)' }}>
        Closing removes this from the state's open obligations without issuing a
        determination — use it when the subgrantee withdraws, not to avoid a decision.
        A release or denial is recorded below, after the review has been run.
      </div>
    </div>
  );
}


const ID_STATUS: Record<string, { label: string; cls: string }> = {
  priceable:            { label: 'Priceable',       cls: 'pill pill--success' },
  already_released:     { label: 'Already released', cls: 'pill pill--warning' },
  in_another_project:   { label: 'Other project',   cls: 'pill pill--warning' },
  in_project_no_geocode:{ label: 'No coordinates',  cls: 'pill pill--warning' },
  unknown:              { label: 'Not found',       cls: 'pill pill--danger' },
  duplicate:            { label: 'Duplicate',       cls: 'pill' },
};

// The request, as rows. Every id the subgrantee sent, what this award makes of it, and
// a way to strike one without retyping the list.
function RequestList({ ids, resolved, onChange }: {
  ids: string[]; resolved: any; onChange: (next: string[]) => void;
}) {
  const [q, setQ] = useState('');
  const status = useMemo(() => {
    const m = new Map<string, string>();
    const put = (arr: any[] | undefined, kind: string) => {
      for (const x of arr ?? []) {
        const id = String(x?.id ?? x);
        if (!m.has(id)) m.set(id, kind);
      }
    };
    // Most specific first: an id that is already released is not simply "priceable".
    put(resolved?.already_released, 'already_released');
    put(resolved?.unknown, 'unknown');
    put(resolved?.in_another_project, 'in_another_project');
    put(resolved?.in_project_no_geocode, 'in_project_no_geocode');
    put(resolved?.duplicates, 'duplicate');
    put(resolved?.priceable, 'priceable');
    return m;
  }, [resolved]);

  if (!ids.length) return null;
  const needle = q.trim();
  const rows = ids.map((id, i) => ({ id, n: i + 1, kind: status.get(id) ?? null }))
    .filter((r) => !needle || r.id.includes(needle));
  const junk = ids.filter((id) => {
    const k = status.get(id);
    return k && k !== 'priceable';
  });

  return (
    <div style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 10,
                    borderBottom: '1px solid var(--border)' }}>
        <b style={{ fontSize: 13 }}>The list · {ids.length}</b>
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Find a number" style={{ width: 200, marginLeft: 8 }} />
        {needle && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rows.length} shown</span>}
        {junk.length > 0 && (
          <button className="btn" style={{ marginLeft: 'auto' }}
                  onClick={() => onChange(ids.filter((id) => (status.get(id) ?? 'priceable') === 'priceable'))}>
            Strike the {junk.length} that cannot be priced
          </button>
        )}
      </div>
      <div style={{ maxHeight: 260, overflowY: 'auto' }}>
        {rows.map((r) => {
          const st = r.kind ? ID_STATUS[r.kind] : null;
          return (
            <div key={r.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px',
              borderBottom: '1px solid var(--border)', fontSize: 13,
            }}>
              <span style={{ width: 34, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                {r.n}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', flex: 1 }}>{r.id}</span>
              {st && <span className={st.cls}>{st.label}</span>}
              <button className="btn" title="Strike this one from the request"
                      onClick={() => onChange(ids.filter((x) => x !== r.id))}>
                Remove
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DeScopePage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();

  const [subgrantee, setSubgrantee] = useState('');
  const [projectId, setProjectId] = useState('');
  const [count, setCount] = useState(200);
  // A real request names specific BSLs. 'remote' is only a what-if convenience for
  // exploring a project the reviewer has not received a formal request for yet.
  const [mode, setMode] = useState<'bsl' | 'remote'>('bsl');
  const [pastedIds, setPastedIds] = useState('');

  // Handoff from the state's Builder. It sends ?project= and ?ids= with the exact set the
  // reviewer lassoed or uploaded, so they arrive here with it loaded instead of re-keying
  // two hundred numbers they just selected on a map. Applied once: after that the list is
  // theirs to edit, and re-applying on every render would fight their edits.
  const [handoffTaken, setHandoffTaken] = useState(false);
  useEffect(() => {
    if (handoffTaken) return;
    const pid = params.get('project');
    // ?ids= is the old form and still works - a link someone saved or pasted into an
    // email has to keep opening.
    let list = (params.get('ids') ?? '').split(',').filter(Boolean);
    if (!list.length && pid && params.get('handoff')) {
      try {
        const raw = sessionStorage.getItem(`descope-handoff:${pid}`);
        if (raw) list = JSON.parse(raw);
      } catch { /* private mode, or a stale key */ }
    }
    if (!pid && !list.length) return;
    if (pid) setProjectId(pid);
    if (list.length) {
      setMode('bsl');
      setPastedIds(list.join(String.fromCharCode(10)));
    }
    setHandoffTaken(true);
    setWantNew(true);
  }, [params, handoffTaken]);

  // Request intake. A de-scope review is a response to something that arrived on a
  // date, from a named person, by some channel - the auditor's first question is
  // "what were you responding to". Captured here and posted with the review so the
  // record carries it, rather than living in the reviewer's inbox.
  const [requesterName, setRequesterName] = useState('');
  const [receivedVia, setReceivedVia] = useState('email');
  const [receivedAt, setReceivedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [statedReason, setStatedReason] = useState('');

  // The review currently on screen. Either just computed, or opened from the queue
  // via /descope?review=<id>.
  const [activeReviewId, setActiveReviewId] = useState<string | null>(params.get('review'));
  const [pendingDecision, setPendingDecision] = useState<string | null>(null);
  const [rationale, setRationale] = useState('');
  const [showCurve, setShowCurve] = useState(false);

  // Item 5/6: the release set. A decision does not release "the review" — it releases
  // named BSLs, and the backend refuses a partial release that does not name them.
  // null = not yet initialised for the review on screen; the effect below seeds it to
  // priced-and-reached, which is also the only default the backend will accept without
  // a separate written reason.
  const [releaseSet, setReleaseSet] = useState<Set<string> | null>(null);
  const [releaseSetTouched, setReleaseSetTouched] = useState(false);
  const [unreachedRationale, setUnreachedRationale] = useState('');
  const [showAllReleaseRows, setShowAllReleaseRows] = useState(false);
  // Item 11: reversal is its own act with its own written reason, never an edit.
  const [reversing, setReversing] = useState(false);
  const [reversalRationale, setReversalRationale] = useState('');

  useEffect(() => {
    const fromUrl = params.get('review');
    if (fromUrl && fromUrl !== activeReviewId) setActiveReviewId(fromUrl);
    // Only reacting to the URL; activeReviewId is intentionally not a dependency,
    // otherwise clearing it locally would immediately re-read the stale param.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const me = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => api.get('/auth/me'),
    retry: false,
  });

  const portfolio = useQuery({
    queryKey: ['descope-portfolio'],
    queryFn: () => api.get('/descope/portfolio'),
  });
  const rows: Row[] = portfolio.data?.subgrantees ?? [];

  // ISP -> Project cascade. Picking the operator first is how a state thinks about it:
  // they hold a portfolio of subgrantees, not a list of projects.
  const subgrantees = useMemo(
    () => Array.from(new Set(rows.map((r) => r.subgrantee))).sort(),
    [rows],
  );
  const projects = useMemo(
    () => rows.filter((r) => !subgrantee || r.subgrantee === subgrantee),
    [rows, subgrantee],
  );
  const project = projects.find((p) => p.project_id === projectId) ?? null;

  const locs = useQuery({
    queryKey: ['descope-locs', projectId],
    queryFn: () => api.get(`/descope/projects/${projectId}/locations`),
    enabled: !!projectId,
  });

  const requestedIds = useMemo(
    () => pastedIds
      .split(/[\s,;]+/)
      .map((x) => x.trim().replace(/^["']|["']$/g, ''))
      .filter((x) => x && !/^(bsl|location_id|fcc_location_id|id)$/i.test(x)),
    [pastedIds],
  );

  // Reconcile the pasted BSLs against the award before anything is priced.
  const resolved = useQuery({
    queryKey: ['descope-resolve', projectId, requestedIds.join(',')],
    queryFn: () => api.post('/descope/resolve', { project_id: projectId, location_ids: requestedIds }),
    enabled: mode === 'bsl' && !!projectId && requestedIds.length > 0,
  });

  // Stand-in for the subgrantee's exclusion request: the N most remote awarded passings.
  const excluded = useMemo(() => {
    const all: Loc[] = locs.data?.locations ?? [];
    if (!all.length) return [];
    const cx = all.reduce((a, l) => a + Number(l.lng), 0) / all.length;
    const cy = all.reduce((a, l) => a + Number(l.lat), 0) / all.length;
    const d = (l: Loc) => Math.hypot(
      (Number(l.lng) - cx) * Math.cos((cy * Math.PI) / 180),
      Number(l.lat) - cy,
    );
    return [...all].sort((a, b) => d(b) - d(a)).slice(0, count);
  }, [locs.data, count]);

  const priceableIds: string[] = mode === 'bsl'
    ? (resolved.data?.priceable ?? [])
    : excluded.map((l) => l.fcc_location_id);

  // ---- the review itself ----
  const review = useMutation<Review>({
    mutationFn: () => api.post('/descope/review', {
      project_id: projectId,
      excluded_location_ids: priceableIds,
      // Intake, sent flat and nested: the record spine stores it on descope_request.
      // Only meaningful for a real (BSL-list) request; a what-if has no requester.
      ...(mode === 'bsl' ? {
        requester_name: requesterName || null,
        received_via: receivedVia || null,
        received_at: receivedAt || null,
        stated_reason: statedReason || null,
        raw_id_list: requestedIds,
        resolve_counts: resolved.data?.counts ?? null,
        request: {
          requester_name: requesterName || null,
          received_via: receivedVia || null,
          received_at: receivedAt || null,
          stated_reason: statedReason || null,
        },
      } : {}),
    }),
    onSuccess: (data) => {
      setPendingDecision(null);
      setRationale('');
      setShowCurve(false);
      if (data?.review_id) {
        setActiveReviewId(data.review_id);
        // Deep-linkable: refreshing the browser must not lose the record, and the
        // oversight queue links straight back in at /descope?review=<id>.
        const next = new URLSearchParams(params);
        next.set('review', data.review_id);
        setParams(next, { replace: true });
      } else {
        setActiveReviewId(null);
      }
    },
  });

  // The persisted record: request + decision. Absent (404) on a box where the record
  // spine has not landed yet, which must degrade to "not recorded", not to a crash.
  const record = useQuery({
    queryKey: ['descope-review-record', activeReviewId],
    queryFn: () => api.get(`/descope/reviews/${activeReviewId}`),
    enabled: !!activeReviewId,
    retry: false,
  });

  // A review opened from the queue arrives only as a stored row; a review just run is
  // in the mutation. Prefer the fresh one, fall back to the persisted response JSON.
  const stored: Review | null =
    record.data?.review?.response_jsonb ?? record.data?.review?.response ?? null;
  const r: Review | null = review.data ?? stored;
  const storedRequest = record.data?.request ?? null;

  const reviewId = r?.review_id ?? record.data?.review?.id ?? activeReviewId ?? null;

  // Opening a stored review from the queue should show which project it belongs to
  // even though the reviewer never touched the dropdowns.
  useEffect(() => {
    const pid = record.data?.review?.project_id;
    if (pid && pid !== projectId) {
      setProjectId(pid);
      const owner = rows.find((x) => x.project_id === pid)?.subgrantee;
      if (owner) setSubgrantee(owner);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.data, rows.length]);

  const decide = useMutation<
    DecisionRow, Error,
    { decision: string; rationale: string; released_ids?: string[]; unreached_rationale?: string }
  >({
    mutationFn: (body) => api.post(`/descope/reviews/${reviewId}/decision`, body),
    onSuccess: () => {
      setPendingDecision(null);
      setRationale('');
      qc.invalidateQueries({ queryKey: ['descope-review-record', activeReviewId] });
      // The oversight queue and the ledger both key off decisions.
      qc.invalidateQueries({ queryKey: ['descope-reviews'] });
    },
  });

  // Item 11: undo. Not a delete and not an edit — the backend restores the prior award
  // state and appends a REVERSING row to the same hash chain, so the original
  // determination and its reversal both stay in the record.
  const reverse = useMutation<any, Error, { rationale: string }>({
    mutationFn: (body) => api.post(`/descope/reviews/${reviewId}/decision/reverse`, body),
    onSuccess: () => {
      setReversing(false);
      setReversalRationale('');
      qc.invalidateQueries({ queryKey: ['descope-review-record', activeReviewId] });
      qc.invalidateQueries({ queryKey: ['descope-reviews'] });
    },
  });

  // The panel locks off the persisted decision, not local state - that is the whole point
  // of item 1. The just-posted row is only a fallback for the beat between the POST
  // returning and the record refetch landing, so the screen never flickers back to
  // "undecided" and invite a second decision on the same review.
  const posted: DecisionRow | null = (() => {
    const d: any = decide.data;
    if (!d) return null;
    const row = d.decision && typeof d.decision === 'object' ? d.decision : d;
    return row && typeof row.decision === 'string' ? (row as DecisionRow) : null;
  })();
  const decision: DecisionRow | null = record.data?.decision ?? posted;

  const curve = useMutation<Curve>({
    mutationFn: () => api.post('/descope/curve', {
      project_id: projectId,
      excluded_location_ids: priceableIds,
      review_id: reviewId,
    }),
  });

  // Changing the inputs invalidates the answer on screen. Clearing the review id too is
  // deliberate: a decision must never be recordable against a result the reviewer has
  // already edited the inputs out from under.
  const clearResult = () => {
    setActiveReviewId(null);
    setPendingDecision(null);
    setRationale('');
    setShowCurve(false);
    setReleaseSet(null);
    setReleaseSetTouched(false);
    setUnreachedRationale('');
    setReversing(false);
    setReversalRationale('');
    curve.reset();
    review.reset();
    reverse.reset();
    if (params.get('review')) {
      const next = new URLSearchParams(params);
      next.delete('review');
      setParams(next, { replace: true });
    }
  };

  // Opening a stored run must restore the inputs it was run on, not just its answer.
  // Guarded by the review id: once restored, the list is the reviewer's to edit and
  // re-applying on every render would fight them.
  const rehydratedFor = useRef<string | null>(null);
  useEffect(() => {
    const rid = record.data?.review?.id;
    if (!rid || rehydratedFor.current === rid) return;
    const resp = record.data?.review?.response ?? record.data?.review?.response_jsonb;
    const ids: string[] = resp?.passings_basis?.resolved_ids
      ?? record.data?.request?.raw_id_list
      ?? [];
    if (!Array.isArray(ids) || !ids.length) return;
    rehydratedFor.current = rid;
    setMode('bsl');
    setPastedIds(ids.map(String).join(String.fromCharCode(10)));
  }, [record.data]);

  // Which of the three things this page is right now. Derived, not stored: a run is
  // open if the URL names one, and the intake form is up only when the reviewer asked
  // for it. Nothing else can put you in the wrong one.
  const [wantNew, setWantNew] = useState(false);
  const view: 'list' | 'run' | 'new' =
    activeReviewId || review.data ? 'run' : (wantNew || params.get('new') === '1') ? 'new' : 'list';

  const backToRecord = () => {
    setWantNew(false);
    clearResult();
    const next = new URLSearchParams(params);
    next.delete('review'); next.delete('new'); next.delete('ids'); next.delete('handoff');
    setParams(next, { replace: false });
    document.querySelector('main')?.scrollTo({ top: 0 });
  };

  const basis = r?.passings_basis ?? null;
  const reached = basis?.reached ?? null;
  const counted = basis?.counted ?? r?.excluded_resolved ?? null;
  const unreached: string[] = Array.isArray(basis?.unreached) ? (basis!.unreached as string[]) : [];
  const warnings: string[] = Array.isArray(r?.design?.warnings) ? (r!.design.warnings as string[]) : [];
  const assumptions = r?.assumptions ?? null;
  const defaultRateCard = assumptions?.source === 'state_default_rate_card';

  // ---- item 2 + item 4: the two denominators, declared ----
  //
  // The design does not reach every requested passing, so there are two honest
  // per-passing figures: over what it reached, and over everything the request counted.
  // They are 40-57% apart on live data. The headline is the reached basis (the money
  // the state can actually evidence a route for); the counted basis is printed right
  // beside it as the like-for-like comparison rather than left in the response body for
  // the subgrantee's counsel to find. Both are read from the declared `bases` block
  // when the backend supplies it, and reconstructed from the flat fields when it does
  // not, so reviews already stored on this box still render.
  const noneReached =
    !!r && (reached === 0 || r.cost.cost_per_passing_denominator === 'none_reached');

  const awardRate = r?.exposure.award_rate_per_passing_usd ?? null;
  const ratioOf = (cpp: number | null | undefined) =>
    cpp != null && awardRate != null && awardRate > 0 ? Number(cpp) / awardRate : null;

  const reachedBasis: CostBasis | null = !r
    ? null
    : noneReached
      ? { cost_per_passing_usd: null, federal_at_risk_usd: null, build_vs_award_ratio: null, n: 0 }
      : {
          n: reached ?? null,
          cost_per_passing_usd: r.bases?.reached?.cost_per_passing_usd ?? r.cost.cost_per_passing_usd ?? null,
          federal_at_risk_usd:
            r.bases?.reached?.federal_at_risk_usd
            ?? (awardRate != null && reached != null ? awardRate * reached : null),
          build_vs_award_ratio:
            r.bases?.reached?.build_vs_award_ratio
            ?? ratioOf(r.bases?.reached?.cost_per_passing_usd ?? r.cost.cost_per_passing_usd),
        };

  const countedCpp =
    r?.bases?.counted?.cost_per_passing_usd
    ?? r?.cost.cost_per_counted_passing_usd
    ?? (r && counted ? Number(r.cost.total_cost_usd) / counted : null);
  const countedBasis: CostBasis | null = !r ? null : {
    n: counted,
    cost_per_passing_usd: countedCpp,
    federal_at_risk_usd:
      r.bases?.counted?.federal_at_risk_usd
      ?? (awardRate != null && counted != null ? awardRate * counted : r.exposure.federal_at_risk_usd),
    build_vs_award_ratio: r.bases?.counted?.build_vs_award_ratio ?? ratioOf(countedCpp),
  };

  // The headline ratio. Null when nothing was reached — item 2: a per-passing figure
  // whose own denominator the same page reports as zero is not a figure at all.
  const ratio = noneReached ? null : (reachedBasis?.build_vs_award_ratio ?? null);
  const supports = ratio != null && ratio > 1;
  // Release is a federal act; it may not be taken off a review that priced nothing.
  const releaseBlocked = noneReached;

  // ---- item 5: what a release would actually name ----
  const resolvedIds: string[] = useMemo(() => {
    const fromBasis = Array.isArray(basis?.resolved_ids)
      ? (basis!.resolved_ids as any[]).map((x) => String(x)).filter(Boolean)
      : [];
    if (fromBasis.length) return fromBasis;
    // Reviews stored before resolved_ids was frozen into the record: fall back to the
    // set on screen only when it is the run that produced this result.
    return review.data ? priceableIds : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basis, review.data, priceableIds.join(',')]);

  const unreachedSet = useMemo(() => new Set(unreached), [unreached.join(',')]);

  const locById = useMemo(() => {
    const m = new Map<string, Loc>();
    for (const l of (locs.data?.locations ?? []) as Loc[]) m.set(l.fcc_location_id, l);
    return m;
  }, [locs.data]);

  // Furthest-from-centroid first — this MUST reproduce backend orderFurthestFirst()
  // (lib/descope-curve.ts) exactly, because "cut line at n" on the curve and "the first
  // n rows in this panel" have to name the same n passings. The curve's point at n is a
  // real engine run over the backend's first n; if this list orders differently, the
  // reviewer ticks a set that was never the set that was costed.
  //
  // Two things here previously diverged from the backend and both changed the order:
  //   1. the centroid is over THIS REVIEW's located ids, not all awarded locations in
  //      the project (941 rows, a different centre entirely); and
  //   2. longitude carries a cos(latitude) correction. At Mississippi's ~32.5° that is
  //      a 16% compression of the x axis, which reorders any pair separated more by
  //      longitude than latitude.
  // Ids with no coordinates cannot be ranked and sort last, exactly as the backend
  // appends its `rest`, so they only enter at the final full-set cut point.
  const orderedReleaseIds: string[] = useMemo(() => {
    if (!resolvedIds.length) return resolvedIds;
    const have = resolvedIds.filter((id) => {
      const l = locById.get(id);
      return l && Number.isFinite(Number(l.lng)) && Number.isFinite(Number(l.lat));
    });
    // Backend bails to as-supplied below two locatable points; match that rather than
    // inventing an order it would not have used.
    if (have.length < 2) return resolvedIds;
    const cLng = have.reduce((a, id) => a + Number(locById.get(id)!.lng), 0) / have.length;
    const cLat = have.reduce((a, id) => a + Number(locById.get(id)!.lat), 0) / have.length;
    const kx = Math.cos((cLat * Math.PI) / 180);
    const d = (id: string) => {
      const l = locById.get(id)!;
      const dx = (Number(l.lng) - cLng) * kx;
      const dy = Number(l.lat) - cLat;
      return dx * dx + dy * dy;
    };
    const ordered = [...have].sort((a, b) => d(b) - d(a));
    const haveSet = new Set(have);
    return [...ordered, ...resolvedIds.filter((id) => !haveSet.has(id))];
  }, [resolvedIds, locById]);

  const reachedIds = useMemo(
    () => orderedReleaseIds.filter((id) => !unreachedSet.has(id)),
    [orderedReleaseIds, unreachedSet],
  );

  // Seed the release set to priced-and-reached whenever the review on screen changes.
  // Deliberately NOT "everything the review resolved": item 6 — releasing a passing the
  // engine never costed a route to is a de-obligation with no evidence behind it.
  //
  // Re-seeding is gated on the reviewer not having touched the list yet. A review opened
  // from the queue renders before /projects/:id/locations resolves, so reachedIds changes
  // shape a beat after the result appears; without this gate that late arrival would
  // silently wipe ticks the analyst had already made.
  useEffect(() => {
    if (releaseSetTouched) return;
    setReleaseSet(new Set(reachedIds));
    setUnreachedRationale('');
    setShowAllReleaseRows(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reachedIds.join(','), releaseSetTouched]);

  // A different review is a different set of facts — its selection always starts fresh.
  useEffect(() => {
    setReleaseSetTouched(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId]);

  const selected = releaseSet ?? new Set<string>();
  const selectedUnreached = useMemo(
    () => [...selected].filter((id) => unreachedSet.has(id)),
    [selected, unreachedSet],
  );
  const toggleRelease = (id: string) => {
    setReleaseSetTouched(true);
    setReleaseSet((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // "Draw the cut line at n": select the n furthest passings, which is exactly the
  // subset the curve priced at n.
  const applyCutLine = (n: number) => {
    setReleaseSetTouched(true);
    setReleaseSet(new Set(orderedReleaseIds.slice(0, Math.max(0, Math.min(n, orderedReleaseIds.length)))));
  };

  // Same shapes as before, but taken from the design system rather than from hard-coded
  // greys. A page that ships its own off-white reads as a mockup sitting next to the
  // product, and it does not follow the theme.
  // The register. Driven by the same endpoint as the oversight queue, narrowed to the
  // project in hand.
  const runsQ = useQuery<any[]>({
    queryKey: ['descope-reviews', projectId],
    // include_analysis: the record shows every run, including the analyst's own
    // what-ifs. The oversight queue deliberately does not.
    queryFn: () => api.get(
      `/descope/reviews?include_analysis=1${projectId ? `&project_id=${projectId}` : ''}`),
    staleTime: 30_000,
  });
  const runs = (runsQ.data ?? [])
    .filter((r: any) => r.id)
    .sort((a: any, b: any) => String(b.run_at ?? '').localeCompare(String(a.run_at ?? '')));

  // Who this office can hand a matter to. The list is people whose role carries
  // grantor.review, so it cannot offer someone who would not be allowed to sign.
  const assigneesQ = useQuery<{ assignees: any[] }>({
    queryKey: ['descope-assignees'],
    queryFn: () => api.get('/descope/assignees'),
    staleTime: 5 * 60 * 1000,
  });

  // The open matter this page is answering, if the run was tied to one.
  const matter = useMemo(
    () => (runsQ.data ?? []).find((r: any) => r.request_id && r.status === 'open'
      && (!projectId || r.project_id === projectId)) ?? null,
    [runsQ.data, projectId],
  );

  // Open a stored run. ?review= is the mechanism the queue already uses, so a row here
  // and a row on the oversight queue land the reviewer in exactly the same place.
  const openRun = (row: any) => {
    const next = new URLSearchParams(params);
    next.set('review', row.review_no ?? row.id);
    if (row.project_id) next.set('project', row.project_id);
    next.delete('ids'); next.delete('handoff');
    setParams(next, { replace: false });
    setActiveReviewId(row.id);
    if (row.project_id) setProjectId(row.project_id);
    // The reviewer clicked something four screens down; put them where it opened.
    document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteRun = useMutation({
    mutationFn: (id: string) => api.del(`/descope/reviews/${id}`),
    onSuccess: (_d, id) => {
      if (activeReviewId === id) clearResult();
      qc.invalidateQueries({ queryKey: ['descope-reviews'] });
      toast.success('Run discarded');
    },
    onError: (e: any) => toast.error(String(e?.message ?? e)),
  });

  const patchRequest = useMutation({
    mutationFn: (body: any) => api.patch(`/descope/requests/${matter?.request_id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['descope-reviews'] });
      toast.success('Matter updated');
    },
    onError: (e: any) => toast.error(String(e?.message ?? e)),
  });


  const card: React.CSSProperties = {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', padding: 16,
  };
  const label: React.CSSProperties = {
    fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em',
    color: 'var(--text-muted)',
  };
  const input: React.CSSProperties = { width: '100%', padding: 8, boxSizing: 'border-box' };

  const rationaleOk = rationale.trim().length >= RATIONALE_MIN;
  const isReleaseKind = (k: string | null) =>
    k === 'grant_release' || k === 'partial_release_outliers_only';
  const releaseKind = isReleaseKind(pendingDecision);
  // A release decision that names nothing releases nothing — and the backend rejects it.
  // Catch it here so the analyst sees a disabled button and a count, not a red 400.
  const releaseSetEmpty = releaseKind && selected.size === 0;
  const unreachedRationaleOk =
    !releaseKind || selectedUnreached.length === 0
    || unreachedRationale.trim().length >= RATIONALE_MIN;
  const canRecord = rationaleOk && !releaseSetEmpty && unreachedRationaleOk;
  const reversalOk = reversalRationale.trim().length >= RATIONALE_MIN;

  return (
    <div style={{ padding: 24, maxWidth: view === 'list' ? 1500 : 1100 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h1 style={{ margin: '0 0 4px' }}>De-Scope Review</h1>
        {reviewId && (
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <b style={{ fontFamily: 'var(--font-mono)', fontSize: 15 }}>
              {record.data?.review?.review_no ?? reviewNoOf(runs, reviewId) ?? 'Review'}
            </b>
            {/* The uuid stays visible but small: an auditor pulling the row needs the
                exact key, everyone else needs the case number. */}
            <span style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
              {reviewId}
            </span>
          </span>
        )}
      </div>
      <p className="page__subtitle" style={{ marginTop: 0 }}>
        {view === 'list'
          ? 'Every de-scope review this office has run. Open one to act on it, or start a new one.'
          : view === 'new'
            ? 'A subgrantee has asked to be released from awarded passings. Price the request independently before deciding.'
            : 'This review as it was run. The figures below are the stored record, not a recalculation.'}
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '0 0 16px' }}>
        {view !== 'list' && (
          <button className="btn" onClick={backToRecord}>&larr; All reviews</button>
        )}
        {view === 'list' && (
          <button className="btn btn--primary" onClick={() => setWantNew(true)}>New review</button>
        )}
      </div>

      {view === 'run' && <MatterActions
        matter={matter}
        assignees={assigneesQ.data?.assignees ?? []}
        busy={patchRequest.isPending}
        onPatch={(body: any) => patchRequest.mutate(body)}
      />}

      {/* ---- 1. who and what — only when composing a new review ---- */}
      {view === 'new' && (
      <div style={{ ...card, display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr auto', alignItems: 'end' }}>
        <div>
          <div style={label}>Subgrantee</div>
          <select value={subgrantee} onChange={(e) => { setSubgrantee(e.target.value); setProjectId(''); clearResult(); }}
                  style={input}>
            <option value="">All subgrantees</option>
            {subgrantees.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <div style={label}>Project</div>
          <select value={projectId} onChange={(e) => { setProjectId(e.target.value); clearResult(); }}
                  style={input}>
            <option value="">Select a project…</option>
            {projects.map((p) => (
              <option key={p.project_id} value={p.project_id}>
                {p.project_code} — {p.awarded_locations} passings
              </option>
            ))}
          </select>
        </div>
        <div>
          <div style={label}>Request</div>
          <select value={mode} onChange={(e) => { setMode(e.target.value as any); clearResult(); }}
                  style={{ width: 200, padding: 8 }}>
            <option value="bsl">By BSL number</option>
            <option value="remote">What-if: N most remote</option>
          </select>
        </div>
      </div>

      )}

      {/* ---- 1b. what arrived, from whom, when ---- */}
      {view === 'new' && project && mode === 'bsl' && (
        <div style={{ ...card, marginTop: 12 }}>
          <div style={{ ...label, marginBottom: 8 }}>The request being reviewed</div>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1.4fr 1fr 1fr' }}>
            <div>
              <div style={label}>Requested by</div>
              <input value={requesterName} onChange={(e) => setRequesterName(e.target.value)}
                     placeholder="Name on the subgrantee's request" style={input} />
            </div>
            <div>
              <div style={label}>Received via</div>
              <select value={receivedVia} onChange={(e) => setReceivedVia(e.target.value)} style={input}>
                <option value="email">Email</option>
                <option value="portal">Portal submission</option>
                <option value="letter">Letter</option>
                <option value="meeting">Meeting / call</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <div style={label}>Date received</div>
              <input type="date" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} style={input} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={label}>Reason the subgrantee states</div>
            <textarea value={statedReason} onChange={(e) => setStatedReason(e.target.value)} rows={2}
                      placeholder="In their words — this is quoted back in the determination."
                      style={{ ...input, fontFamily: 'inherit' }} />
          </div>
        </div>
      )}

      {view === 'new' && project && mode === 'bsl' && (
        <div style={{ ...card, marginTop: 12 }}>
          <div style={label}>BSL / FCC location numbers the subgrantee wants released</div>
          <textarea
            value={pastedIds}
            onChange={(e) => { setPastedIds(e.target.value); clearResult(); }}
            placeholder={'Paste from their request - commas, spaces, or one per line.\n1234567890\n1234567891, 1234567892'}
            rows={5}
            style={{ ...input, fontFamily: 'ui-monospace, monospace', fontSize: 13, padding: 10 }}
          />
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
            <label style={{ fontSize: 13, cursor: 'pointer' }}>
              <input type="file" accept=".csv,.txt" style={{ display: 'none' }}
                     onChange={(e) => {
                       const f = e.target.files?.[0];
                       if (!f) return;
                       const fr = new FileReader();
                       fr.onload = () => { setPastedIds(String(fr.result || '')); clearResult(); };
                       fr.readAsText(f);
                     }} />
              <span style={{ textDecoration: 'underline' }}>…or upload their CSV</span>
            </label>
            <span style={{ color: '#6b7280', fontSize: 13 }}>{requestedIds.length} numbers read</span>
          </div>

          <RequestList
            ids={requestedIds}
            resolved={resolved.data}
            onChange={(next) => { setPastedIds(next.join(String.fromCharCode(10))); clearResult(); }}
          />

          {resolved.data && (
            <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <span><b>{resolved.data.counts.priceable}</b> priceable</span>
                {resolved.data.counts.in_project_no_geocode > 0 &&
                  <span style={{ color: '#b45309' }}><b>{resolved.data.counts.in_project_no_geocode}</b> not geocoded</span>}
                {resolved.data.counts.in_another_project > 0 &&
                  <span style={{ color: '#b45309' }}><b>{resolved.data.counts.in_another_project}</b> in another project</span>}
                {resolved.data.counts.unknown > 0 &&
                  <span style={{ color: '#b91c1c' }}><b>{resolved.data.counts.unknown}</b> not found</span>}
                {resolved.data.duplicates?.length > 0 &&
                  <span style={{ color: '#b45309' }}><b>{resolved.data.duplicates.length}</b> duplicated</span>}
                {/* Item 9: the overlap that actually happens in the field — a re-submitted
                    list that still contains passings this office already released. It is
                    NOT a typo and it is NOT priceable, and if it is silently dropped the
                    same denominator produces a different dollar figure. */}
                {(resolved.data.counts.already_released ?? 0) > 0 &&
                  <span style={{ color: '#b45309' }}>
                    <b>{resolved.data.counts.already_released}</b> already released
                  </span>}
              </div>
              {(resolved.data.counts.already_released ?? 0) > 0 && (
                <div style={{
                  fontSize: 12, padding: 10, borderRadius: 8,
                  background: '#fffbeb', border: '1px solid #fcd34d', color: '#78350f',
                }}>
                  <b>{resolved.data.counts.already_released} of these passings are no longer awarded</b> —
                  this office released them in an earlier determination, so they cannot be released
                  again and are not costed here. Confirm the subgrantee is not re-submitting a list
                  it already had granted before you price the remainder.
                  {Array.isArray(resolved.data.already_released) && resolved.data.already_released.length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      {resolved.data.already_released.slice(0, 6).map((x: any, i: number) => (
                        <div key={i} style={{ fontFamily: 'ui-monospace, monospace' }}>
                          {String(x?.id ?? x)}
                          {x?.decided_at ? ` — released ${new Date(x.decided_at).toLocaleDateString()}` : ''}
                          {x?.decision_id ? ` (determination ${String(x.decision_id).slice(0, 8)})` : ''}
                        </div>
                      ))}
                      {resolved.data.already_released.length > 6 && (
                        <div>…and {resolved.data.already_released.length - 6} more.</div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {(resolved.data.counts.unknown > 0 || resolved.data.counts.in_another_project > 0
                || resolved.data.counts.in_project_no_geocode > 0) && (
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Only the priceable ones are costed. Anything unmatched is listed here rather than
                  dropped silently, because it changes how many passings the request really covers:
                  {resolved.data.unknown?.length > 0 && <> not found — <code>{resolved.data.unknown.slice(0, 8).join(', ')}</code>.</>}
                  {resolved.data.in_another_project?.length > 0 && <> other project — <code>{resolved.data.in_another_project.slice(0, 5).map((x: any) => `${x.id} (${x.project_code})`).join(', ')}</code>.</>}
                  {resolved.data.in_project_no_geocode?.length > 0 && <> no coordinates — <code>{resolved.data.in_project_no_geocode.slice(0, 5).join(', ')}</code>.</>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {view === 'new' && project && mode === 'remote' && (
        <div style={{ ...card, marginTop: 12, display: 'flex', gap: 16, alignItems: 'end' }}>
          <div>
            <div style={label}>How many of the most remote passings</div>
            <input type="number" min={1} value={count}
                   onChange={(e) => { setCount(Math.max(1, Number(e.target.value) || 1)); clearResult(); }}
                   style={{ width: 140, padding: 8 }} />
          </div>
          <div style={{ color: '#6b7280', fontSize: 13 }}>
            Exploratory only — use “By BSL number” for an actual request.
          </div>
        </div>
      )}

      {view === 'new' && project && (
        <div style={{ ...card, marginTop: 12, display: 'flex', gap: 32 }}>
          <div><div style={label}>Award</div><b>{usd(Number(project.grant_amount_usd))}</b></div>
          {/* Item 25: these two are different numbers and the difference is the point.
              awarded_bsl_count is what the subgrant obligated; awarded_locations is what
              is still obligated after prior releases. Shipping them side by side unlabelled
              reads as an inconsistency; labelled, it reads as the register working. */}
          <div>
            <div style={label}>Passings — original award</div>
            <b>{project.awarded_bsl_count ?? '—'}</b>
          </div>
          <div>
            <div style={label}>Passings — currently awarded</div>
            <b>{project.awarded_locations}</b>
            {project.awarded_bsl_count != null
              && Number(project.awarded_bsl_count) !== Number(project.awarded_locations) && (
              <div style={{ fontSize: 11, color: '#9ca3af' }}>
                {Number(project.awarded_bsl_count) - Number(project.awarded_locations)} released
                by prior determination
              </div>
            )}
          </div>
          <div><div style={label}>Under review</div><b>{priceableIds.length}</b></div>
          <div style={{ marginLeft: 'auto' }}>
            <button
              className="btn btn--primary"
              onClick={() => review.mutate()}
              disabled={!projectId || !priceableIds.length || review.isPending}
              style={{ padding: '10px 18px', fontWeight: 600 }}>
              {review.isPending ? 'Designing…' : 'Run independent review'}
            </button>
          </div>
        </div>
      )}

      {review.isPending && (
        <p style={{ color: '#6b7280' }}>
          Routing fibre to those passings over the real road network — this takes a moment.
        </p>
      )}
      {review.isError && (
        <p style={{ color: '#b91c1c' }}>{String((review.error as any)?.message || review.error)}</p>
      )}
      {!r && activeReviewId && record.isPending && (
        <p style={{ color: '#6b7280', marginTop: 16 }}>Opening review {activeReviewId}…</p>
      )}
      {!r && activeReviewId && record.isError && (
        <p style={{ color: '#b91c1c', marginTop: 16 }}>
          Could not open review {activeReviewId}: {String((record.error as any)?.message || record.error)}
        </p>
      )}

      {/* ---- 2. the answer ---- */}
      {r && (
        <>
          <div style={{
            ...card, marginTop: 16,
            borderColor: releaseBlocked ? '#fcd34d' : supports ? '#fca5a5' : '#86efac',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
              <div>
                <div style={label}>Cost to build</div>
                <div style={{ fontSize: 26, fontWeight: 700 }}>{usd(r.cost.total_cost_usd)}</div>
              </div>
              <div>
                <div style={label}>Cost per reached passing</div>
                <div style={{ fontSize: 26, fontWeight: 700 }}>
                  {releaseBlocked ? '—' : usd(reachedBasis?.cost_per_passing_usd)}
                </div>
                <div style={{ fontSize: 11, color: releaseBlocked ? '#b45309' : '#9ca3af' }}>
                  {releaseBlocked
                    ? `the design reached 0 of ${counted ?? '—'} — no per-passing figure`
                    : `over the ${reached ?? '—'} passings the design reached`}
                </div>
              </div>
              <div>
                <div style={label}>Award rate per passing</div>
                <div style={{ fontSize: 26, fontWeight: 700 }}>{usd(r.exposure.award_rate_per_passing_usd)}</div>
              </div>
              <div>
                <div style={label}>Federal $ at risk</div>
                <div style={{ fontSize: 26, fontWeight: 700 }}>
                  {releaseBlocked
                    ? '—'
                    : usd(reachedBasis?.federal_at_risk_usd ?? r.exposure.federal_at_risk_usd)}
                </div>
                <div style={{ fontSize: 11, color: releaseBlocked ? '#b45309' : '#9ca3af' }}>
                  {releaseBlocked
                    ? 'nothing was reached — no exposure can be stated on this basis'
                    : `to de-obligate — on the same ${reached ?? 0} reached passings`}
                </div>
              </div>
            </div>

            {/* ITEM 2 — nothing was reached. The cost is real, the per-passing figure is
                not, and neither is any release drawn off it. This replaces the headline
                rather than sitting under it. */}
            {releaseBlocked && (
              <div style={{
                marginTop: 16, padding: 12, borderRadius: 8,
                background: '#fffbeb', border: '1px solid #fcd34d',
              }}>
                <b>No cost per passing.</b> The design reached <b>0</b> of {counted ?? '—'} requested
                locations, so there is no denominator and no ratio to the award rate.{' '}
                <b>This review does not support a release.</b> Re-run it with a head-end that can
                actually serve these locations, or record a decision that does not de-obligate.
              </div>
            )}

            {/* ITEM 4 — one document, two denominators, both declared. Reached is the
                headline because it is the only basis with a costed route behind every
                passing; counted is printed beside it because it is the number the
                subgrantee will quote back, and it must not be discoverable only in JSON. */}
            {!releaseBlocked && (
              <div style={{
                marginTop: 16, borderRadius: 8, overflow: 'hidden',
                border: `1px solid ${supports ? '#fecaca' : '#bbf7d0'}`,
              }}>
                <div style={{
                  padding: 12, background: supports ? '#fef2f2' : '#f0fdf4',
                }}>
                  {ratio != null ? (
                    <>
                      <b>{ratio.toFixed(2)}× the award rate</b>, on the reached basis.{' '}
                      {supports
                        ? 'Building these passings costs more than the award assumed — the request is supported by the cost evidence.'
                        : 'Building these passings is within the award rate — the cost evidence does not support release.'}
                    </>
                  ) : (
                    <>The ratio to the award rate could not be computed for this review.</>
                  )}
                </div>
                <div style={{ padding: 12, fontSize: 13, background: '#fff', borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ ...label, marginBottom: 6 }}>Both denominators, stated</div>
                  <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: '#6b7280' }}>
                        <th style={{ textAlign: 'left', padding: '2px 14px 2px 0', fontWeight: 500 }}>Basis</th>
                        <th style={{ textAlign: 'right', padding: '2px 14px', fontWeight: 500 }}>Passings</th>
                        <th style={{ textAlign: 'right', padding: '2px 14px', fontWeight: 500 }}>Cost / passing</th>
                        <th style={{ textAlign: 'right', padding: '2px 14px', fontWeight: 500 }}>Ratio</th>
                        <th style={{ textAlign: 'right', padding: '2px 0 2px 14px', fontWeight: 500 }}>Federal $ at risk</th>
                      </tr>
                    </thead>
                    <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
                      <tr style={{ fontWeight: 700 }}>
                        <td style={{ padding: '3px 14px 3px 0' }}>Reached by the design</td>
                        <td style={{ textAlign: 'right', padding: '3px 14px' }}>{reachedBasis?.n ?? '—'}</td>
                        <td style={{ textAlign: 'right', padding: '3px 14px' }}>{usd(reachedBasis?.cost_per_passing_usd)}</td>
                        <td style={{ textAlign: 'right', padding: '3px 14px' }}>
                          {reachedBasis?.build_vs_award_ratio != null
                            ? reachedBasis.build_vs_award_ratio.toFixed(2) + '×' : '—'}
                        </td>
                        <td style={{ textAlign: 'right', padding: '3px 0 3px 14px' }}>{usd(reachedBasis?.federal_at_risk_usd)}</td>
                      </tr>
                      <tr style={{ color: '#374151' }}>
                        <td style={{ padding: '3px 14px 3px 0' }}>Counted in the request</td>
                        <td style={{ textAlign: 'right', padding: '3px 14px' }}>{countedBasis?.n ?? '—'}</td>
                        <td style={{ textAlign: 'right', padding: '3px 14px' }}>{usd(countedBasis?.cost_per_passing_usd)}</td>
                        <td style={{ textAlign: 'right', padding: '3px 14px' }}>
                          {countedBasis?.build_vs_award_ratio != null
                            ? countedBasis.build_vs_award_ratio.toFixed(2) + '×' : '—'}
                        </td>
                        <td style={{ textAlign: 'right', padding: '3px 0 3px 14px' }}>{usd(countedBasis?.federal_at_risk_usd)}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p style={{ margin: '8px 0 0', color: '#6b7280' }}>
                    The headline figure and the exposure above are both on the <b>reached</b> basis —
                    the same denominator on the numerator and the denominator of the ratio. The
                    counted row prices the same build over every passing the request named, including
                    the {unreached.length} the design could not route to; it is the like-for-like
                    figure the subgrantee is likely to quote.
                  </p>
                  {/* ITEM 16 — the allocation bracket, when the engine reports it. */}
                  {(r.cost.cost_per_passing_standalone_usd != null
                    || r.cost.cost_per_passing_allocated_usd != null) && (
                    <p style={{ margin: '8px 0 0', color: '#374151' }}>
                      <b>Allocation bracket.</b> Charging the whole build to the excluded set gives{' '}
                      {usd(r.cost.cost_per_passing_standalone_usd ?? reachedBasis?.cost_per_passing_usd)} per
                      passing; spreading it over every passing this plant serves, including those the
                      subgrantee retains, gives {usd(r.cost.cost_per_passing_allocated_usd)}. The true
                      marginal cost of these passings lies between those two figures.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Whose unit costs priced this. The single question the subgrantee's counsel
                asks, so it is a sentence at full weight, not a footnote. */}
            <div style={{
              marginTop: 12, padding: 12, borderRadius: 8, fontSize: 13,
              background: defaultRateCard ? '#fffbeb' : '#f9fafb',
              border: `1px solid ${defaultRateCard ? '#fcd34d' : '#e5e7eb'}`,
            }}>
              {assumptions?.source == null ? (
                <>This review did not report which rate card priced it. Treat the unit costs as
                unattributed until the review is re-run against a version that records them.</>
              ) : defaultRateCard ? (
                <>
                  <b>Priced with the state's default rate card</b>
                  {assumptions.rate_card_name ? <> ({assumptions.rate_card_name})</> : null}
                  {assumptions.region_mult != null ? <>, region multiplier {Number(assumptions.region_mult).toFixed(2)}</> : null}.
                  {' '}The subgrantee's own unit costs were not on file for this plant, so this
                  estimate does <b>not</b> use their pricing. Say so in any correspondence that
                  quotes the number.
                </>
              ) : (
                <>
                  <b>Priced with the subgrantee's own rate card</b>
                  {assumptions.rate_card_name ? <> ({assumptions.rate_card_name})</> : null}
                  {assumptions.region_mult != null ? <>, region multiplier {Number(assumptions.region_mult).toFixed(2)}</> : null}
                  {assumptions.plant_settings_from ? <>, copied from their plant settings ({assumptions.plant_settings_from})</> : null}
                  {' '}— the same build standards they are held to under the subgrant.
                </>
              )}
            </div>

            {r.exposure.award_rate_basis && (
              <p style={{ marginBottom: 0, marginTop: 12, fontSize: 13, color: '#374151' }}>
                The award rate of {usd(r.exposure.award_rate_per_passing_usd)} per passing is derived
                from the {String(r.exposure.award_rate_basis).replace(/_/g, ' ')}.
              </p>
            )}

            {r.method_note && (
              <p style={{ marginBottom: 0, marginTop: 8, fontSize: 13, color: '#374151' }}>
                <b>Method{r.method ? ` — ${String(r.method).replace(/_/g, ' ')}` : ''}.</b> {r.method_note}
              </p>
            )}

            <p style={{ marginBottom: 0, marginTop: 8, fontSize: 13, color: '#374151' }}>
              {seededFromProse(r.seeded_from)}
              {' '}The design produced {r.design.segments ?? '—'} segments
              {r.design.length_km ? `, ${r.design.length_km.toFixed(1)} km of plant` : ''}
              {r.structures_passed_by_designed_plant != null
                ? `, passing ${r.structures_passed_by_designed_plant.toLocaleString()} structures in total`
                : ''}
              {r.shared_with_retained != null
                ? ` — ${r.shared_with_retained.toLocaleString()} of which are also passed by the plant the subgrantee retains`
                : ''}
              .
            </p>

            {/* Requested vs counted vs reached. The denominator is the argument. */}
            <div style={{ marginTop: 12, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }}>
              <span><b>{basis?.requested ?? r.excluded_requested}</b> requested</span>
              <span><b>{basis?.counted ?? r.excluded_resolved}</b> counted</span>
              {reached != null && (
                <span style={{ color: unreached.length ? '#b45309' : undefined }}>
                  <b>{reached}</b> reached by the design
                </span>
              )}
              {unreached.length > 0 && (
                <span style={{ color: '#b45309' }}><b>{unreached.length}</b> not reached</span>
              )}
            </div>
            {unreached.length > 0 && (
              <details style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                <summary style={{ cursor: 'pointer' }}>
                  {unreached.length} requested passings had no structure the design could route to —
                  they are excluded from the per-passing figure. Show the numbers.
                </summary>
                <code style={{ display: 'block', marginTop: 6, wordBreak: 'break-all' }}>
                  {unreached.join(', ')}
                </code>
              </details>
            )}

            {warnings.length > 0 && (
              <div style={{
                marginTop: 12, padding: 12, borderRadius: 8,
                background: '#fffbeb', border: '1px solid #fcd34d', fontSize: 13,
              }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  The design engine raised {warnings.length} warning{warnings.length === 1 ? '' : 's'} on this run
                </div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
          </div>

          {/* ---- 3. the cut line ---- */}
          <div style={{ ...card, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ ...label, margin: 0 }}>Cut line</div>
              <button
                onClick={() => { setShowCurve(true); curve.mutate(); }}
                disabled={curve.isPending || !priceableIds.length}
                style={{ marginLeft: 'auto', padding: '8px 14px', cursor: 'pointer' }}>
                {curve.isPending ? 'Designing at each size…' : 'Compute cost curve'}
              </button>
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 0 }}>
              The request is a single number; the decision does not have to be. This re-designs the
              same set at several sizes, furthest passings first, so a partial release can be drawn
              where cost per passing stops justifying it.
            </p>
            {showCurve && curve.isError && (
              <p style={{ color: '#b91c1c', fontSize: 13 }}>
                Cost curve unavailable: {String((curve.error as any)?.message || curve.error)}
              </p>
            )}
            {showCurve && curve.data?.points?.length ? (
              <CurvePlot
                curve={curve.data}
                awardRate={r.exposure.award_rate_per_passing_usd}
                onPick={decision || releaseBlocked ? undefined : applyCutLine}
                picked={releaseSetTouched ? selected.size : null}
              />
            ) : null}
          </div>

          {/* ---- 3b. what a release would actually name (item 5 + item 6) ----
               The cut-line panel above promises a partial release can be drawn at the
               cut line; this is where it is drawn. Every release decision posts these
               ids. Unreached passings are listed but start unticked: releasing one is a
               de-obligation the engine produced no costed route for, and it takes its
               own written reason. */}
          {reviewId && !decision && orderedReleaseIds.length > 0 && (
            <div style={{ ...card, marginTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ ...label, margin: 0 }}>Passings this decision would release</div>
                <div style={{ marginLeft: 'auto', fontSize: 13 }}>
                  <b style={{ fontSize: 18 }}>{selected.size}</b> of {orderedReleaseIds.length} selected
                  {selectedUnreached.length > 0 && (
                    <span style={{ color: '#b45309' }}>
                      {' '}— including {selectedUnreached.length} the design never reached
                    </span>
                  )}
                </div>
              </div>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '6px 0 10px' }}>
                Ordered furthest-first, the same order the cost curve releases in. Starts at the{' '}
                {reachedIds.length} passings the design both priced <i>and</i> reached — releasing a
                passing it could not route to has no cost evidence behind it, so those are listed
                separately below and are not selected for you.
              </p>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                <button onClick={() => { setReleaseSetTouched(true); setReleaseSet(new Set(reachedIds)); }}
                        disabled={releaseBlocked}
                        style={{ padding: '5px 10px', cursor: 'pointer' }}>
                  All {reachedIds.length} reached
                </button>
                <button onClick={() => { setReleaseSetTouched(true); setReleaseSet(new Set()); }}
                        style={{ padding: '5px 10px', cursor: 'pointer' }}>
                  None
                </button>
                <label style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
                  Cut line at
                  <input
                    // Uncontrolled so typing "1"→"12"→"120" does not fight the field, but
                    // re-keyed on the review so it never shows a stale default from the
                    // last one.
                    key={`${reviewId}:${reachedIds.length}`}
                    type="number" min={0} max={orderedReleaseIds.length}
                    defaultValue={reachedIds.length}
                    onChange={(e) => applyCutLine(Number(e.target.value) || 0)}
                    disabled={releaseBlocked}
                    style={{ width: 84, padding: 5 }}
                  />
                  furthest
                </label>
                {curve.data?.points?.length ? (
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    (curve priced {curve.data.points.map((p) => p.n).join(', ')})
                  </span>
                ) : null}
              </div>

              {releaseBlocked && (
                <div style={{
                  padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 10,
                  background: '#fffbeb', border: '1px solid #fcd34d', color: '#78350f',
                }}>
                  The design reached none of these locations, so none of them can be released on
                  this review's evidence.
                </div>
              )}

              <div style={{
                maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8,
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {(showAllReleaseRows ? orderedReleaseIds : orderedReleaseIds.slice(0, 40)).map((id, i) => {
                      const l = locById.get(id);
                      const isUnreached = unreachedSet.has(id);
                      return (
                        <tr key={id} style={{ borderTop: i ? '1px solid #f3f4f6' : undefined }}>
                          <td style={{ padding: '5px 8px', width: 28 }}>
                            <input
                              type="checkbox"
                              checked={selected.has(id)}
                              disabled={releaseBlocked}
                              onChange={() => toggleRelease(id)}
                            />
                          </td>
                          <td style={{ padding: '5px 8px', width: 44, color: '#9ca3af' }}>{i + 1}</td>
                          <td style={{ padding: '5px 8px', fontFamily: 'ui-monospace, monospace', width: 130 }}>{id}</td>
                          <td style={{ padding: '5px 8px', color: l ? '#374151' : '#9ca3af' }}>
                            {l ? [l.address, l.city].filter(Boolean).join(', ') || 'address not recorded'
                               : 'no longer in the awarded set'}
                          </td>
                          <td style={{ padding: '5px 8px', width: 130, textAlign: 'right' }}>
                            {isUnreached
                              ? <span style={{ color: '#b45309', fontSize: 12 }}>not reached</span>
                              : <span style={{ color: '#15803d', fontSize: 12 }}>reached</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {orderedReleaseIds.length > 40 && (
                <button onClick={() => setShowAllReleaseRows((v) => !v)}
                        style={{ marginTop: 8, padding: '4px 10px', cursor: 'pointer' }}>
                  {showAllReleaseRows
                    ? 'Show first 40'
                    : `Show all ${orderedReleaseIds.length}`}
                </button>
              )}
            </div>
          )}

          {/* ---- 4. the decision ---- */}
          <div style={{ ...card, marginTop: 12 }}>
            <div style={{ ...label, marginBottom: 8 }}>Decision</div>

            {decision ? (
              <div>
                <div style={{
                  padding: 12, borderRadius: 8,
                  background: decision.reversed_at ? '#f9fafb' : '#f0f9ff',
                  border: `1px solid ${decision.reversed_at ? '#d1d5db' : '#bae6fd'}`,
                }}>
                  {decision.reversed_at && (
                    <div style={{
                      fontSize: 12, fontWeight: 700, color: '#b91c1c', letterSpacing: '.06em',
                      textTransform: 'uppercase', marginBottom: 6,
                    }}>
                      Reversed on {new Date(decision.reversed_at).toLocaleDateString()}
                      {decision.reversed_by_name ? ` by ${decision.reversed_by_name}` : ''}
                    </div>
                  )}
                  <div style={{
                    fontSize: 18, fontWeight: 700,
                    textDecoration: decision.reversed_at ? 'line-through' : undefined,
                    color: decision.reversed_at ? '#6b7280' : undefined,
                  }}>
                    {decisionLabel(decision.decision)}
                  </div>
                  <div style={{ fontSize: 13, color: '#374151', marginTop: 6, whiteSpace: 'pre-wrap' }}>
                    {decision.rationale}
                  </div>
                  {Array.isArray(decision.released_ids) && (
                    <div style={{ fontSize: 13, color: '#374151', marginTop: 8 }}>
                      <b>{decision.released_ids.length}</b> passing
                      {decision.released_ids.length === 1 ? '' : 's'} de-obligated by this
                      determination
                      {decision.released_ids.length > 0 && (
                        <details style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                          <summary style={{ cursor: 'pointer' }}>Show the numbers released</summary>
                          <code style={{ display: 'block', marginTop: 6, wordBreak: 'break-all' }}>
                            {decision.released_ids.join(', ')}
                          </code>
                        </details>
                      )}
                    </div>
                  )}
                  {decision.unreached_rationale && (
                    <div style={{ fontSize: 12, color: '#78350f', marginTop: 8 }}>
                      <b>Released without a costed route</b> — stated reason: {decision.unreached_rationale}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
                    Decided by {decision.decided_by_name || decision.decided_by_email
                      || (decision.decided_by && decision.decided_by === me.data?.id ? me.data?.email : decision.decided_by)
                      || 'unrecorded reviewer'}
                    {decision.decided_at ? ` on ${new Date(decision.decided_at).toLocaleString()}` : ''}.
                    This review is closed; re-run the review to produce a new record.
                  </div>
                  {decision.reversal_rationale && (
                    <div style={{ fontSize: 12, color: '#374151', marginTop: 8, whiteSpace: 'pre-wrap' }}>
                      <b>Reversal reason:</b> {decision.reversal_rationale}
                    </div>
                  )}
                </div>
                {reviewId && (
                  <p style={{ marginBottom: 0, marginTop: 12 }}>
                    <a href={`${API_BASE}/descope/reviews/${reviewId}/determination.pdf`}
                       target="_blank" rel="noreferrer"
                       style={{ fontWeight: 600 }}>
                      Download Determination (PDF)
                    </a>
                    <span style={{ color: '#6b7280', fontSize: 12 }}>
                      {' '}— the letter to the subgrantee and the auditor's exhibit.
                    </span>
                  </p>
                )}
                {/* Item 11: a release un-awards federal locations. Nothing in the product
                    could put them back, which made every mis-click permanent. This does
                    not erase the determination — it appends a reversing entry to the same
                    chain and restores the award state the decision captured. */}
                {reviewId && !decision.reversed_at && (
                  <div style={{ marginTop: 12 }}>
                    <button onClick={() => { setReversing(true); setReversalRationale(''); reverse.reset(); }}
                            style={{
                              padding: '6px 12px', cursor: 'pointer', fontSize: 13,
                              border: '1px solid #d1d5db', background: '#fff', borderRadius: 6,
                            }}>
                      Reverse this determination
                    </button>
                    <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 8 }}>
                      Restores the award state this decision changed and records the reversal as its
                      own entry. The original determination stays in the ledger.
                    </span>
                  </div>
                )}
              </div>
            ) : reviewId ? (
              <>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {DECISIONS.map(([k, lbl]) => {
                    // Item 2: a release cannot be recorded off a review that reached nothing.
                    const blocked = releaseBlocked && isReleaseKind(k);
                    return (
                      <button key={k}
                              disabled={blocked}
                              title={blocked
                                ? 'The design reached 0 locations — this review does not support a release.'
                                : undefined}
                              onClick={() => {
                                setPendingDecision(k);
                                setRationale('');
                                decide.reset();
                              }}
                              style={{
                                padding: '8px 14px', cursor: blocked ? 'not-allowed' : 'pointer',
                                border: '1px solid #d1d5db',
                                background: blocked ? '#f3f4f6' : '#fff',
                                color: blocked ? '#9ca3af' : undefined,
                                borderRadius: 6,
                              }}>
                        {lbl}
                      </button>
                    );
                  })}
                </div>
                <p style={{ marginBottom: 0, color: '#6b7280', fontSize: 13 }}>
                  Every decision is written to the permanent record against review {reviewId}, with
                  the reviewer, the timestamp, a written rationale, and — for a release — the exact
                  list of passings it de-obligates.
                </p>
              </>
            ) : (
              <p style={{ marginBottom: 0, color: '#b45309', fontSize: 13 }}>
                This result was not written to the review record, so a decision cannot be recorded
                against it. Re-run the review once the record spine is available on this box.
              </p>
            )}
          </div>

          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 12 }}>{r.disclaimer}</p>

          {storedRequest && (
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              Request received{storedRequest.received_at ? ` ${new Date(storedRequest.received_at).toLocaleDateString()}` : ''}
              {storedRequest.received_via ? ` via ${storedRequest.received_via}` : ''}
              {storedRequest.requester_name ? ` from ${storedRequest.requester_name}` : ''}
              {storedRequest.stated_reason ? ` — stated reason: “${storedRequest.stated_reason}”` : ''}
            </p>
          )}
        </>
      )}

      {/* ---- decision modal: a rationale is not optional ---- */}
      {pendingDecision && (
        <div
          onClick={() => { if (!decide.isPending) setPendingDecision(null); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 20,
          }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ ...card, width: 620, maxWidth: '100%', boxShadow: '0 20px 50px rgba(0,0,0,.3)' }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>{decisionLabel(pendingDecision)}</h2>
            <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0 }}>
              {r ? (
                <>
                  {reached != null
                    ? `${reached} of ${counted ?? r.excluded_resolved} passings reached`
                    : `${counted ?? r.excluded_resolved} passings`} ·{' '}
                  {usd(r.cost.total_cost_usd)} to build ·{' '}
                  {releaseBlocked ? 'no per-passing figure' : `${usd(reachedBasis?.cost_per_passing_usd)} per reached passing`}
                  {ratio != null ? ` · ${ratio.toFixed(2)}× the award rate` : ''}
                </>
              ) : null}
            </p>

            {/* Item 5: what this decision de-obligates, named, before it is recorded. */}
            {releaseKind && (
              <div style={{
                padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13,
                background: releaseSetEmpty ? '#fef2f2' : '#f9fafb',
                border: `1px solid ${releaseSetEmpty ? '#fecaca' : '#e5e7eb'}`,
              }}>
                {releaseSetEmpty ? (
                  <>
                    <b>No passings are selected.</b> A release must name the BSLs it de-obligates.
                    Close this and tick them in <i>Passings this decision would release</i>.
                  </>
                ) : (
                  <>
                    This releases <b>{selected.size}</b> of the {orderedReleaseIds.length} passings this
                    review priced
                    {awardRate != null
                      ? <> — approximately <b>{usd(awardRate * selected.size)}</b> of federal funds
                          de-obligated at the award rate of {usd(awardRate)} per passing</>
                      : null}.
                    {' '}They are un-awarded when you record this, and the list is written into the
                    determination.
                  </>
                )}
              </div>
            )}

            {/* Item 6: an unreached passing has no costed route. Releasing one is allowed,
                but it is a separate decision and it takes a separate written reason, which
                the determination prints under its own heading. */}
            {releaseKind && selectedUnreached.length > 0 && (
              <div style={{
                padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13,
                background: '#fffbeb', border: '1px solid #fcd34d',
              }}>
                <b>{selectedUnreached.length} of the selected passings were never reached by the
                design</b>, so this review produced no costed route for them. Releasing them is a
                de-obligation the cost evidence does not cover, and it needs its own reason.
                <textarea
                  value={unreachedRationale}
                  onChange={(e) => setUnreachedRationale(e.target.value)}
                  rows={3}
                  placeholder="Why these specific passings are released without a costed route."
                  style={{ ...input, fontFamily: 'inherit', marginTop: 6 }}
                />
                <div style={{ fontSize: 12, color: unreachedRationaleOk ? '#6b7280' : '#b45309' }}>
                  {unreachedRationaleOk
                    ? `${unreachedRationale.trim().length} characters`
                    : `${unreachedRationale.trim().length} / ${RATIONALE_MIN} characters minimum.`}
                </div>
              </div>
            )}

            <div style={label}>Rationale (goes into the record and the determination letter)</div>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              rows={5}
              autoFocus
              placeholder="Why this decision, on this evidence. Name the figures you relied on."
              style={{ ...input, fontFamily: 'inherit', marginTop: 4 }}
            />
            <div style={{ fontSize: 12, color: rationaleOk ? '#6b7280' : '#b45309', marginTop: 4 }}>
              {rationaleOk
                ? `${rationale.trim().length} characters`
                : `${rationale.trim().length} / ${RATIONALE_MIN} characters minimum — a decision without a written reason is not defensible in a federal record.`}
            </div>
            {decide.isError && (
              <p style={{ color: '#b91c1c', fontSize: 13 }}>
                {String((decide.error as any)?.message || decide.error)}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button onClick={() => setPendingDecision(null)} disabled={decide.isPending}
                      style={{ padding: '8px 14px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={() => decide.mutate({
                  decision: pendingDecision,
                  rationale: rationale.trim(),
                  // Item 5: the release set travels with the decision. Without it the
                  // backend either 400s (partial release) or falls back to releasing
                  // everything the review resolved, including passings it never priced
                  // a route to (item 6).
                  ...(releaseKind ? { released_ids: [...selected] } : {}),
                  ...(releaseKind && selectedUnreached.length > 0
                    ? { unreached_rationale: unreachedRationale.trim() }
                    : {}),
                })}
                disabled={!canRecord || decide.isPending}
                style={{
                  padding: '8px 16px', fontWeight: 600,
                  cursor: canRecord && !decide.isPending ? 'pointer' : 'not-allowed',
                }}>
                {decide.isPending
                  ? 'Recording…'
                  : releaseKind
                    ? `Record decision — release ${selected.size}`
                    : 'Record decision'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- reversal modal (item 11) ---- */}
      {reversing && decision && (
        <div
          onClick={() => { if (!reverse.isPending) setReversing(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 20,
          }}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ ...card, width: 620, maxWidth: '100%', boxShadow: '0 20px 50px rgba(0,0,0,.3)' }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>Reverse this determination</h2>
            <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0 }}>
              This restores the award state the determination changed
              {Array.isArray(decision.released_ids) && decision.released_ids.length > 0
                ? ` — ${decision.released_ids.length} passing${decision.released_ids.length === 1 ? '' : 's'} return to the award`
                : ''}
              . The original determination is <b>not</b> deleted: it stays in the ledger and the
              reversal is appended as its own entry, so the record shows both acts and the order
              they happened in.
            </p>
            <div style={label}>Why this determination is being reversed</div>
            <textarea
              value={reversalRationale}
              onChange={(e) => setReversalRationale(e.target.value)}
              rows={5}
              autoFocus
              placeholder="What changed, or what was wrong with the original determination."
              style={{ ...input, fontFamily: 'inherit', marginTop: 4 }}
            />
            <div style={{ fontSize: 12, color: reversalOk ? '#6b7280' : '#b45309', marginTop: 4 }}>
              {reversalOk
                ? `${reversalRationale.trim().length} characters`
                : `${reversalRationale.trim().length} / ${RATIONALE_MIN} characters minimum — a reversal without a written reason is as undefendable as the decision it undoes.`}
            </div>
            {reverse.isError && (
              <p style={{ color: '#b91c1c', fontSize: 13 }}>
                {String((reverse.error as any)?.message || reverse.error)}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button onClick={() => setReversing(false)} disabled={reverse.isPending}
                      style={{ padding: '8px 14px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={() => reverse.mutate({ rationale: reversalRationale.trim() })}
                disabled={!reversalOk || reverse.isPending}
                style={{
                  padding: '8px 16px', fontWeight: 600,
                  cursor: reversalOk && !reverse.isPending ? 'pointer' : 'not-allowed',
                }}>
                {reverse.isPending ? 'Reversing…' : 'Record reversal'}
              </button>
            </div>
          </div>
        </div>
      )}

      <RunRecord
        primary={view === 'list'}
        runs={runs}
        loading={runsQ.isLoading}
        projectId={projectId}
        activeId={activeReviewId}
        onOpen={openRun}
        onDelete={(id: string) => deleteRun.mutate(id)}
        busy={deleteRun.isPending}
      />
    </div>
  );
}

// Inline SVG rather than a charting dependency: this plot ends up printed as an exhibit,
// and a two-series line chart does not justify shipping a library into the bundle.
function CurvePlot({ curve, awardRate, onPick, picked }: {
  curve: Curve;
  awardRate: number | null;
  // Item 5: the curve is where the cut line is chosen, so it is also where it should be
  // applied. Picking a point seeds the release set to exactly the n furthest passings
  // that point was priced on.
  onPick?: (n: number) => void;
  picked?: number | null;
}) {
  const pts = [...(curve.points ?? [])]
    .filter((p) => Number.isFinite(Number(p.n)) && Number.isFinite(Number(p.cost_per_passing)))
    .sort((a, b) => a.n - b.n);
  if (pts.length < 2) {
    return <p style={{ fontSize: 13, color: '#6b7280' }}>Not enough points to plot a curve.</p>;
  }

  const W = 660, H = 280, ML = 74, MR = 20, MT = 18, MB = 42;
  const iw = W - ML - MR, ih = H - MT - MB;

  const xs = pts.map((p) => p.n);
  const ys = pts.map((p) => p.cost_per_passing);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  // The award rate is the line the decision turns on, so it must always be in frame even
  // when the curve sits an order of magnitude above it.
  const yMax = Math.max(...ys, awardRate ?? 0) * 1.08;
  const x = (n: number) => ML + (xMax === xMin ? iw / 2 : ((n - xMin) / (xMax - xMin)) * iw);
  const y = (v: number) => MT + ih - (yMax <= 0 ? 0 : (v / yMax) * ih);

  const path = pts.map((p, i) => `${i ? 'L' : 'M'}${x(p.n).toFixed(1)},${y(p.cost_per_passing).toFixed(1)}`).join(' ');
  const be = curve.breakeven_n != null && Number.isFinite(Number(curve.breakeven_n))
    ? Number(curve.breakeven_n) : null;
  const beInFrame = be != null && be >= xMin && be <= xMax;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * yMax);

  return (
    <div style={{ marginTop: 12, overflowX: 'auto' }}>
      <svg width={W} height={H} role="img" aria-label="Cost per passing versus number of passings released">
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={ML} x2={W - MR} y1={y(t)} y2={y(t)} stroke="#e5e7eb" />
            <text x={ML - 8} y={y(t) + 4} textAnchor="end" fontSize="10" fill="#6b7280">
              {t >= 1000 ? `$${Math.round(t / 1000)}k` : `$${Math.round(t)}`}
            </text>
          </g>
        ))}

        {awardRate != null && (
          <>
            <line x1={ML} x2={W - MR} y1={y(awardRate)} y2={y(awardRate)}
                  stroke="#16a34a" strokeWidth={1.5} strokeDasharray="5 4" />
            <text x={W - MR} y={y(awardRate) - 6} textAnchor="end" fontSize="11" fill="#16a34a">
              award rate {awardRate.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}/passing
            </text>
          </>
        )}

        <path d={path} fill="none" stroke="#2563eb" strokeWidth={2} />
        {pts.map((p, i) => (
          <g key={i} onClick={onPick ? () => onPick(p.n) : undefined}
             style={onPick ? { cursor: 'pointer' } : undefined}>
            {onPick && (
              <circle cx={x(p.n)} cy={y(p.cost_per_passing)} r={11} fill="transparent">
                <title>Draw the cut line at {p.n} passings</title>
              </circle>
            )}
            <circle cx={x(p.n)} cy={y(p.cost_per_passing)}
                    r={picked === p.n ? 6 : 4}
                    fill={picked === p.n ? '#b45309' : '#2563eb'} />
            <text x={x(p.n)} y={y(p.cost_per_passing) - 10} textAnchor="middle" fontSize="10" fill="#1f2937">
              {p.cost_per_passing >= 1000
                ? `$${Math.round(p.cost_per_passing / 1000)}k`
                : `$${Math.round(p.cost_per_passing)}`}
            </text>
            <text x={x(p.n)} y={H - MB + 16} textAnchor="middle" fontSize="10" fill="#6b7280">{p.n}</text>
          </g>
        ))}

        {beInFrame && (
          <>
            <line x1={x(be!)} x2={x(be!)} y1={MT} y2={MT + ih} stroke="#b45309" strokeDasharray="4 3" />
            <text x={x(be!) + 6} y={MT + 12} fontSize="11" fill="#b45309">breakeven at {Math.round(be!)}</text>
          </>
        )}

        <line x1={ML} x2={W - MR} y1={MT + ih} y2={MT + ih} stroke="#9ca3af" />
        <text x={ML + iw / 2} y={H - 6} textAnchor="middle" fontSize="11" fill="#6b7280">
          passings released (furthest first)
        </text>
      </svg>

      <p style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
        {be == null ? (
          curve.breakeven_unavailable_reason ? (
            <>
              <b>No breakeven can be stated from this curve.</b>{' '}
              {curve.breakeven_unavailable_reason}. Read the points as four independent estimates at
              four sizes, not as a curve you can interpolate a cut line off.
            </>
          ) : (
            <>Cost per passing does not fall to the award rate anywhere in the range tested — every
            subset of this request costs more to build than the award priced it at.</>
          )
        ) : (
          <>
            Cost per passing crosses the award rate at approximately <b>{Math.round(be)}</b> passings
            {curve.breakeven_cost_per_passing != null
              ? ` (${curve.breakeven_cost_per_passing.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })} per passing)`
              : ''}
            . Releasing beyond that point is the part the cost evidence supports.
          </>
        )}
      </p>
      {onPick && (
        <p style={{ fontSize: 12, color: '#6b7280', marginTop: 0 }}>
          Click a point to draw the cut line there — the release list below is set to exactly the
          furthest passings that point was priced on.
        </p>
      )}
    </div>
  );
}
